import { z } from 'zod';
import {
  BasisPoints,
  Instant,
  MinorUnits,
  MinorUnitsNonNegative,
  PageRequest,
  pageOf,
  ServiceDay,
  UserPreferences,
  Uuid,
} from '../common';
import { DeliveryJobStatus, KillSwitchScope, RatingSubject } from '../enums';
import { FilterFacet } from '../facets';
import { defineEndpoint } from '../http';
import { InternalAlertKind } from '../internal-alert-kinds';
import { ProviderCapabilityDeclaration } from '../models';
import { patchSchema } from '../patch';

// ---------------------------------------------------------------------------
// Kill switch and suspension (PRD OPS-009, OPS-003)
// ---------------------------------------------------------------------------

export const KillSwitchState = z.object({
  scope: KillSwitchScope,
  targetId: Uuid.nullable(),
  targetLabel: z.string().max(160).nullable(),
  engaged: z.boolean(),
  engagedAt: Instant.nullable(),
  engagedBy: z.string().max(160).nullable(),
});
export type KillSwitchState = z.infer<typeof KillSwitchState>;

export const listKillSwitches = defineEndpoint({
  method: 'GET',
  path: '/internal/kill-switch',
  mount: 'internal',
  summary: 'Every engaged stop-accepting-orders scope, visible in the admin dashboard (OPS-009)',
  response: z.object({ switches: z.array(KillSwitchState) }),
});

export const setKillSwitch = defineEndpoint({
  method: 'POST',
  path: '/internal/kill-switch',
  mount: 'internal',
  summary: 'Engage or release the kill switch at global, city, bakery or property scope (OPS-009)',
  body: z.object({
    scope: KillSwitchScope,
    /**
     * The scope's target: an organization/property id for BAKERY and PROPERTY,
     * the CITY NAME for CITY (a routing key in its own field, never inferred
     * from anything else), null for GLOBAL.
     */
    targetId: z.string().min(1).max(160).nullable(),
    engaged: z.boolean(),
  }),
  idempotent: true,
  response: KillSwitchState,
});

// ---------------------------------------------------------------------------
// Readiness check (PRD OPS-010, NTF-006)
// ---------------------------------------------------------------------------

export const ReadinessCheck = z.object({
  ranAt: Instant,
  serviceDay: ServiceDay,
  green: z.boolean(),
  conditions: z.array(
    z.object({
      key: z.enum([
        'ALL_ORDERS_ALLOCATED',
        'ALL_ORDERS_ACCEPTED',
        'TABLET_HEARTBEATS',
        'PROVIDER_API_REACHABLE',
        'WEBHOOK_ENDPOINTS_REACHABLE',
        'DEAD_LETTER_EMPTY',
        'NO_PENDING_CAPTURES',
      ]),
      passed: z.boolean(),
      detail: z.string().max(400),
    }),
  ),
});
export type ReadinessCheck = z.infer<typeof ReadinessCheck>;

export const getReadiness = defineEndpoint({
  method: 'GET',
  path: '/internal/readiness',
  mount: 'internal',
  summary: 'Latest pre-service readiness result, one green/red verdict (OPS-010)',
  query: z.object({ serviceDay: ServiceDay.optional() }),
  response: ReadinessCheck,
});

/**
 * RTE-033: how the provider's arrivals sat against what was predicted over
 * the last `days` - the number to read before loosening a window or a margin,
 * and the one place that says whether the couriers are keeping the platform's
 * promises (the windows sent are tighter than the provider recommends).
 */
export const ArrivalAccuracy = z.object({
  days: z.number().int().min(1).max(90),
  since: Instant,
  count: z.number().int().min(0),
  /** Seconds; positive = late against the prediction. Null with no arrivals. */
  meanDeltaSeconds: z.number().int().nullable(),
  medianDeltaSeconds: z.number().int().nullable(),
  p90DeltaSeconds: z.number().int().nullable(),
  /** More than five minutes after / before the prediction. */
  lateCount: z.number().int().min(0),
  earlyCount: z.number().int().min(0),
});
export type ArrivalAccuracy = z.infer<typeof ArrivalAccuracy>;

export const getArrivalAccuracy = defineEndpoint({
  method: 'GET',
  path: '/internal/arrivals',
  mount: 'internal',
  summary: 'Predicted-versus-actual courier arrival over the last days (RTE-033)',
  query: z.object({ days: z.coerce.number().int().min(1).max(90).default(7) }),
  response: ArrivalAccuracy,
});

export const runReadiness = defineEndpoint({
  method: 'POST',
  path: '/internal/readiness/run',
  mount: 'internal',
  summary: 'Run the readiness check on demand; also fires the alert (OPS-010, NTF-006)',
  body: z.object({ serviceDay: ServiceDay.optional(), notify: z.boolean().default(false) }),
  idempotent: true,
  response: ReadinessCheck,
});

// ---------------------------------------------------------------------------
// Operations alerts (PRD OPS-011, BAK-020, NTF-003, ALC-006, SUP-003)
// ---------------------------------------------------------------------------

/**
 * One alert the platform raised for a human to act on.
 *
 * Distinct from `AnomalyAlert` above, which is OPS-017's self-serve abuse
 * detection and nothing else. These are the operational ones - a missing tablet
 * heartbeat (OPS-011, BAK-020), a courier no-show (SUP-003), an order nobody can
 * fulfil (ALC-006), a lateness risk (RTE-006), a held payout - and until this
 * endpoint existed the only one with a reader was the order-scoped kind, via the
 * incident count on a dashboard row. A location-scoped alert has no order,
 * so it matched nothing and no surface could show it (OPS-011 requires one).
 *
 * `kind` is a member of the ONE closed list (`INTERNAL_ALERT_KINDS`, founder
 * decision alert-titles = B): every member carries an English title in
 * Internal Ops, which shows the title and then the code in parentheses. It was
 * a free string, and an untitled kind reached an operator as its raw code.
 */
export const OperationsAlert = z.object({
  id: z.string().min(1).max(200),
  kind: InternalAlertKind,
  raisedAt: Instant,
  /** Set when the alert is about one order (most of them). */
  orderId: Uuid.nullable(),
  shortCode: z.string().max(10).nullable(),
  /** Set when the alert is about a place rather than an order (OPS-011). */
  bakeryLocationId: Uuid.nullable(),
  bakeryLocationName: z.string().max(160).nullable(),
  /** The producer's payload, flattened to scalars; shape varies by kind. */
  detail: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  /**
   * Whether an operator has taken this one off the board (OPS-011).
   *
   * Until it existed the list could only grow: the dedupe key means a raised
   * alert can never be raised again, and nothing could mark one dealt with, so
   * an incident closed at 06:40 sat at the top of the screen for the rest of
   * the retention window. Acknowledging is not resolving and never changes what
   * the alert says - the row is the record of what the platform noticed, and
   * this is the record of somebody having seen it.
   */
  acknowledged: z.boolean(),
  acknowledgedAt: Instant.nullable(),
  /** Whoever acted, as the audit row recorded them. */
  acknowledgedBy: z.string().max(160).nullable(),
});
export type OperationsAlert = z.infer<typeof OperationsAlert>;

export const acknowledgeAlert = defineEndpoint({
  method: 'POST',
  path: '/internal/alerts/:alertId/ack',
  mount: 'internal',
  summary: 'Take one operations alert off the board; idempotent (OPS-011)',
  params: z.object({
    /**
     * The alert's own id, which is the producer's DEDUPE KEY
     * (`location:<id>:heartbeat-missing:<t>`) rather than a uuid - that is
     * exactly what the outbox row was deduped on, and it is the only stable
     * handle an alert has in either runtime mode.
     */
    alertId: z.string().min(1).max(200),
  }),
  body: z.object({ note: z.string().max(500).default('') }),
  idempotent: true,
  /**
   * Idempotent by re-answering the same acknowledgement rather than by
   * refusing: acknowledging an alert twice is one operator clicking twice, and
   * a 409 there would be a puzzle rather than a protection. The FIRST
   * acknowledgement's instant and operator are what the reply carries.
   */
  response: OperationsAlert,
});

// ---------------------------------------------------------------------------
// Dead-letter queue and provider events (PRD OPS-002, RTE-004)
// ---------------------------------------------------------------------------

export const DeadLetterEvent = z.object({
  id: Uuid,
  queue: z.string().max(80),
  /** External event id; the unique constraint that makes replay idempotent. */
  externalId: z.string().max(200).nullable(),
  kind: z.string().max(80),
  failedAt: Instant,
  attempts: z.int().nonnegative(),
  lastError: z.string().max(2000),
  payload: z.record(z.string(), z.unknown()),
  replayedAt: Instant.nullable(),
});
export type DeadLetterEvent = z.infer<typeof DeadLetterEvent>;

export const listDeadLetter = defineEndpoint({
  method: 'GET',
  path: '/internal/dead-letter',
  mount: 'internal',
  summary: 'View failed webhook and outbox events (OPS-002, OPS-010)',
  query: PageRequest.extend({ queue: z.string().max(80).optional() }),
  response: pageOf(DeadLetterEvent),
});

export const replayDeadLetter = defineEndpoint({
  method: 'POST',
  path: '/internal/dead-letter/replay',
  mount: 'internal',
  summary: 'Queue one dead-lettered event for retry (OPS-063, RTE-004)',
  /* One dead letter at a time (founder, 14 October 2026). */
  body: z.object({ eventId: Uuid }),
  idempotent: true,
  /**
   * `replayed` counts messages queued for retry, not completed processing.
   * Keep the wire name for already deployed clients. `failed` and `errors`
   * describe enqueue failures only; later processing failures are dead letters.
   */
  response: z.object({
    replayed: z.int().nonnegative(),
    failed: z.int().nonnegative(),
    errors: z.array(z.object({ eventId: Uuid, error: z.string().max(2000) })).default([]),
  }),
});

/**
 * OPS-002 / OPS-010: close a dead letter no replay can ever drain.
 *
 * Without it one permanently undeliverable message - an `order.ready` whose
 * promised window has closed, say - keeps `DEAD_LETTER_EMPTY` red for ever and
 * every replay attempt records one more row. The discard lands in the audit log
 * with its operator and the events it closed (OPS-008).
 */
export const discardDeadLetter = defineEndpoint({
  method: 'POST',
  path: '/internal/dead-letter/discard',
  mount: 'internal',
  summary: 'Discard one dead-lettered event that can never be replayed (OPS-002, OPS-010)',
  /* One dead letter at a time (founder, 14 October 2026). */
  body: z.object({ eventId: Uuid }),
  idempotent: true,
  response: z.object({ discarded: z.int().nonnegative(), failed: z.int().nonnegative() }),
});

export const getProviderStatus = defineEndpoint({
  method: 'GET',
  path: '/internal/providers',
  mount: 'internal',
  summary:
    'Active delivery provider and its declared capabilities and fallbacks (RTE-022, RTE-023)',
  response: z.object({
    /** Null when the platform setting names no adapter this deploy holds (RTE-023). */
    active: z.string().max(60).nullable(),
    declarations: z.array(ProviderCapabilityDeclaration),
    recentJobs: z.array(
      z.object({
        orderId: Uuid,
        /** The code the order is called by everywhere else (BAK-017). */
        shortCode: z.string().max(10),
        status: DeliveryJobStatus,
        quotedFee: MinorUnitsNonNegative.nullable(),
        chargedFee: MinorUnitsNonNegative.nullable(),
        /** PRD RTE-027 - divergence beyond a threshold raises an alert. */
        feeDivergence: MinorUnits.nullable(),
      }),
    ),
  }),
});

// ---------------------------------------------------------------------------
// Quality and ratings (PRD OPS-007, GST-038)
// ---------------------------------------------------------------------------

export const listRatings = defineEndpoint({
  method: 'GET',
  path: '/internal/ratings',
  mount: 'internal',
  summary:
    'Guest ratings feeding quality reporting; internal QA, never published (OPS-007, GST-038)',
  query: PageRequest.extend({
    /** Any of these subjects; omitted or empty means both. Repeated on the wire. */
    subject: z.array(RatingSubject).max(RatingSubject.options.length).optional(),
    bakeryLocationId: Uuid.optional(),
    from: ServiceDay.optional(),
    to: ServiceDay.optional(),
    maxStars: z.int().min(1).max(5).optional(),
  }),
  response: z.object({
    averages: z.object({
      delivery: z.number().min(0).max(5).nullable(),
      products: z.number().min(0).max(5).nullable(),
    }),
    ratings: pageOf(
      z.object({
        id: Uuid,
        orderId: Uuid,
        /** The code the order is called by everywhere else (BAK-017). */
        shortCode: z.string().max(10),
        subject: RatingSubject,
        stars: z.int().min(1).max(5),
        comment: z.string().max(2000).nullable(),
        /** The house that prepared the order, in either storefront mode. */
        bakeryName: z.string().max(160).nullable(),
        createdAt: Instant,
      }),
    ),
    /**
     * How many ratings of each subject the window holds (founder, 2026-09-14:
     * a subject no rating carries is not offered). Counted over the whole
     * from/to window with `maxStars` applied and the subject choice ignored,
     * never over one page. A subject with no rating is absent rather than zero.
     */
    facets: z.object({ subject: z.array(FilterFacet) }),
  }),
});

export const getQualityReport = defineEndpoint({
  method: 'GET',
  path: '/internal/quality',
  mount: 'internal',
  summary: 'On-time, preparation delay, courier wait, failure causes, scorecards (OPS-007)',
  /** `bakeryLocationId` scopes every figure to one counter; absent = platform. */
  query: z.object({ from: ServiceDay, to: ServiceDay, bakeryLocationId: Uuid.optional() }),
  response: z.object({
    /**
     * Platform-wide on-time deliveries of all deliveries in the window, and
     * NULL when nothing was delivered in it - never a zero, which would read
     * as every delivery late (founder, #2125 decisions, report-rate-floors C).
     * A real 0 % stays 0.
     */
    onTimeBasisPoints: BasisPoints.nullable(),
    /**
     * The window's preparation figure at the SAME quantile the platform
     * promises on (`COUNTER_TIMING_QUANTILE`), so this report and the bookings
     * made during it are the same statistic. It was the median, which put a
     * different number on the quality screen from the one every courier that
     * week had been sent for.
     *
     * NULLABLE, and unlike the per-counter surfaces there is no seed to fall
     * back to: this figure is an aggregate over whatever the window held, and
     * an aggregate over nothing is not a fast platform. It answered `?? 0`
     * until a `bakeryLocationId` scope arrived and the zero started being said
     * about a NAMED shop.
     */
    preparationMinutes: z.number().nonnegative().nullable(),
    /** Ready tap to courier collection; null on the same rule. */
    medianCourierWaitMinutes: z.number().nonnegative().nullable(),
    failedDeliveries: z.int().nonnegative(),
    missingItems: z.int().nonnegative(),
    byBakery: z.array(
      z.object({
        locationId: Uuid,
        name: z.string().max(160),
        orders: z.int().nonnegative(),
        /** Null when this shop delivered nothing in the window; a real 0 stays 0. */
        onTimeBasisPoints: BasisPoints.nullable(),
        /** Refusals of the orders the shop held; null only over no order at all. */
        rejectionBasisPoints: BasisPoints.nullable(),
        averageProductRating: z.number().min(0).max(5).nullable(),
      }),
    ),
  }),
});

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Audit log (PRD OPS-008, NFR-011)
// ---------------------------------------------------------------------------

export const listAuditEvents = defineEndpoint({
  method: 'GET',
  path: '/internal/audit',
  mount: 'internal',
  summary:
    'Immutable audit log for pricing, stock, order state, instructions, refunds, access (OPS-008)',
  query: PageRequest.extend({
    subjectType: z.string().max(60).optional(),
    subjectId: z.string().max(200).optional(),
    from: Instant.optional(),
    to: Instant.optional(),
  }),
  response: pageOf(
    z.object({
      id: Uuid,
      at: Instant,
      /**
       * WHO acted, exactly as the row was written - an identity provider's own
       * user id for a signed-in operator, `dev:...` for a bypass, a service
       * name for the platform itself. Never rewritten: the log is append-only
       * and this is the value that identifies the actor for ever.
       */
      actor: z.string().max(160),
      /**
       * ...and the same actor as a person, resolved at READ time. Null when the
       * actor is not a resolvable account, or when the identity provider could
       * not be reached - which is why it is a SECOND field rather than a
       * rewritten `actor`: a name is the readable half, and losing it must not
       * lose the identity the record was written with. Reading the log meant
       * reading `user_3HlTtCggQmDEDcFdSk9sK8b6zbu` and knowing who that was.
       */
      actorName: z.string().max(160).nullable(),
      action: z.string().max(80),
      subjectType: z.string().max(60),
      /*
       * Generous on purpose: audit rows are IMMUTABLE, so the read contract
       * must accept every subject id ever written, not only the shape written
       * today. A `.max(80)` here once made one 97-char historical row reject
       * the whole page client-side and the audit screen could never recover
       * (OPS-008). New writers keep subject ids compact; the reader forgives.
       */
      subjectId: z.string().max(500),
      /**
       * ...and the same subject NAMED, resolved at read time - the shop, the
       * apartment, the house, the order's short code.
       *
       * Beside `subjectId` for the reason `actorName` sits beside `actor`: the
       * log is append-only, the id is the value the record was written with and
       * must never be rewritten, and a name is the half a human can read. The
       * Subject column showed `device` over `0556fc07-7776-47a3-8c75-...` on
       * every row, which named a table and nothing in it.
       *
       * Null when nothing could name it - a rate, a kill switch,
       * or a subject whose record has since been deleted - and the screen then
       * falls back to the id, which is what it printed for all of them before.
       */
      subjectName: z.string().max(200).nullable(),
      /** The subject's relevant fields before the change; null on a creation. */
      before: z.record(z.string(), z.unknown()).nullable(),
      /** ...and after it; null on a deletion. */
      after: z.record(z.string(), z.unknown()).nullable(),
      /** Context that is neither state: a count, a selector, a template key. */
      detail: z.record(z.string(), z.unknown()).nullable(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// The operator's own preferences (PRD NFR-006)
// ---------------------------------------------------------------------------

/**
 * An operator's own language, stored against them rather than against a browser.
 *
 * The internal portal had no per-user setting at all: its language was a select
 * in the top bar backed by `localStorage`, so it was a property of the machine
 * you happened to sign in from. The same shape serves `/c` - one preferences
 * contract, two mounts, because the user pools differ but the preference does
 * not (two Clerk applications, one per signed-in staff portal).
 */
export const getPreferences = defineEndpoint({
  method: 'GET',
  path: '/internal/me/preferences',
  mount: 'internal',
  summary: "The signed-in operator's own language (NFR-006)",
  response: UserPreferences,
});

export const updatePreferences = defineEndpoint({
  method: 'PATCH',
  path: '/internal/me/preferences',
  mount: 'internal',
  summary: "Change the signed-in operator's own language (NFR-006)",
  body: patchSchema(UserPreferences),
  idempotent: false,
  response: UserPreferences,
});

// ---------------------------------------------------------------------------

// Error message keys the internal surface can answer with (NFR-006)
// ---------------------------------------------------------------------------

/**
 * Every `messageKey` an operator's browser can receive from the `/internal` mount -
 * the internal routes' own refusals plus everything the shared error mapper, the
 * endpoint binder and the typed client itself can emit.
 *
 * The list now lives in `../messages`, the platform-wide registry, and is
 * re-exported here because that is where the internal-admin catalogue test and the
 * `internalError` helper already look for it. The registry is what closes the set:
 * `ApiError.messageKey` is a `z.enum` built from it, so an unregistered key
 * fails contract validation rather than reaching a screen untranslated.
 */
export { INTERNAL_ERROR_MESSAGE_KEYS, type InternalErrorMessageKey } from '../messages';
